import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  createFeaturedArticleRequest, getFeaturedArticleByIdRequest, getFeaturedArticlesRequest, sendFeaturedArticleNotification, updateFeaturedArticleRequest,
} from '../../axios/featuredArticleRequests';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  convertCountriesToISOCodeString,
} from '../../constants/countries';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import {
  translate,
} from '../../helperFunctions/translation';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import {
  CreateFeaturedArticleBody, emptyFeaturedArticle, IFeaturedArticle, IFeaturedArticlesStore, isImageArray, UpdateFeaturedArticleBody,
} from '../storeInterfaces/featuredArticlesStore';
import { RequestStatus } from 'mobx-store';

export class FeaturedArticlesStore implements IFeaturedArticlesStore {
  @observable featuredArticlesList: IFeaturedArticle[] = [];

  @observable selectedFeaturedArticle: any = emptyFeaturedArticle;

  @observable loading = false;

  @observable getFeaturedArticlesRequestStatus: RequestStatus = RequestStatus.IDLE;

  @action
  public async getFeaturedArticles(countries: ICountry[], status: 'all' | 'published' | 'unpublished' = 'all',
    langCode: string = DefaultLanguage): Promise<void> {
    try {
      this.getFeaturedArticlesRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);

      this.loading = true;
      const params = {
        availableCountries,
        status,
        type: 'featured',
      };
      const config = {
        ...getHeader(langCode), params,
      };
      const response = await Connections.wadiDeglaAxios.httpGet(getFeaturedArticlesRequest, config);
      this.featuredArticlesList = response.data;
      console.log(response.data);
      this.getFeaturedArticlesRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getFeaturedArticlesRequestStatus = RequestStatus.FAILED;
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getHistoryFeaturedArticles(countries: ICountry[], langCode: string = DefaultLanguage): Promise<void> {
    try {
      const availableCountries = convertCountriesToISOCodeString(countries);
      this.loading = true;
      const params = {
        availableCountries,
        status: 'unpublished',
        type: 'featured',
      };
      const config = {
        ...getHeader(langCode), params,
      };
      const response = await Connections.wadiDeglaAxios.httpGet(getFeaturedArticlesRequest, config);
      console.log(this.restructureHistoryArticles(response.data));
      this.featuredArticlesList = this.restructureHistoryArticles(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getFeaturedArticleById(featuredArticleId: number, langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const params = {
        type: 'featured',
      };
      const config = {
        ...getHeader(langCode), params,
      };
      const response = await Connections.wadiDeglaAxios.httpGet(getFeaturedArticleByIdRequest(featuredArticleId), config);
      this.selectedFeaturedArticle = response.data;
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createFeaturedArticle(newFeaturedArticle: CreateFeaturedArticleBody, langCode: string = DefaultLanguage): Promise<any> {
    return new Promise<void>(async (res, rej) => {
      try {
        if (isImageArray(newFeaturedArticle.imageURL)) {
          newFeaturedArticle.imageURL = await this.uploadSingleImage(newFeaturedArticle.imageURL);
        }
        const response = await Connections.wadiDeglaAxios.httpPost(createFeaturedArticleRequest, newFeaturedArticle, getHeader(langCode));
        res(response.data.featuredArticleId);
      } catch (error) {
        rej(error);
      }
    });
  }

  public async sendNotification(
    featuredArticleId: number,
    titleEn: string,
    bodyEn: string,
    titleAr: string,
    bodyAr: string,
    audience: 'members' | 'nonmembers' | 'all',
    type: string,
    navigationItemType: string,
    navigationItemId: number,
    countries: ICountry[],
  ): Promise<any> {
    try {
      const data = {
        titleEn,
        bodyEn,
        titleAr,
        bodyAr,
        audience,
        type,
        entityType: navigationItemType,
        entityId: navigationItemId,
        availableCountries: countries.map((country: ICountry) => country.isoCode),
      };
      await Connections.wadiDeglaAxios.httpPost(sendFeaturedArticleNotification(featuredArticleId), data);
    } catch (error) {
      console.log(error);
    }
  }

  @action.bound
  public async updateFeaturedArticle(featuredArticleId: number, updatedFeaturedArticle: UpdateFeaturedArticleBody,
    langCode: string = DefaultLanguage): Promise<any> {
    try {
      if (isImageArray(updatedFeaturedArticle.imageURL)) {
        updatedFeaturedArticle.imageURL = await this.uploadSingleImage(updatedFeaturedArticle.imageURL);
      }
      await Connections.wadiDeglaAxios.httpPut(updateFeaturedArticleRequest(featuredArticleId), updatedFeaturedArticle, getHeader(langCode));
    } catch (error) {
      console.log(error);
    }
  }

  @action.bound
  public async updateFeaturedArticleStatus(updatedFeaturedArticleStatus: boolean, index: number): Promise<any> {
    try {
      const newStatus: UpdateFeaturedArticleBody = {
        isActive: updatedFeaturedArticleStatus,
      };
      await Connections.wadiDeglaAxios.httpPut(updateFeaturedArticleRequest(this.featuredArticlesList[index].featuredArticleId), newStatus);
      if (this.featuredArticlesList && this.featuredArticlesList[index]) {
        this.featuredArticlesList[index].isActive = updatedFeaturedArticleStatus;
      }
    } catch (error) {
      console.log(error);
    }
  }

  private uploadSingleImage(images: any[]): Promise<string> {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push(res.data.downloadURL);
            }),
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return (allImages.length ? allImages[0] : null);
      }).catch((error) => {
        console.log('error');
        return (allImages.length ? allImages[0] : null);
      });
  }

  private restructureHistoryArticles = (featuredArticles: any) => featuredArticles.map((article: IFeaturedArticle) => ({
    ...article,
    isActive: (article.isActive) ? 'Active' : 'Not Active',
    edit: translate('featuredArticlesEditButton', {
    }),
  }));
}
